import { LitElement, css, html, nothing } from 'lit';
import { ROLE_NAMES, hasRole } from '../core/auth.js';
import { sharedStyles } from '../styles/shared.js';
import './qz-button.js';

/**
 * Wygląd powłoki aplikacji: nagłówek, miejsce na widok, pasek zakładek, stopka.
 *
 * Komponent jest czysty — o sesji, adresie i wersji dowiaduje się wyłącznie
 * z właściwości, a o kliknięciach w konto informuje zdarzeniami. Router,
 * store'y i nasłuch sesji spina `qz-app`, dlatego powłokę da się oglądać
 * i testować bez backendu.
 *
 * Nagłówek trzyma wyłącznie to, czego używa się w trakcie gry: nazwę, drogę
 * do widoków i konto. Ustawienia — motyw i dźwięk — mieszkają w profilu,
 * bo przestawia się je raz i nie mają po co zajmować pierwszego rzędu
 * na telefonie.
 *
 * @prop {{ name: string, displayName?: string, role: string } | null} user
 *   — zalogowany użytkownik; `null` to gość
 * @prop {boolean} loading — sesja jeszcze wczytywana: nie pokazujemy ani
 *   konta, ani wejścia dla gościa, żeby nic nie mignęło
 * @prop {string} path — bieżąca ścieżka; link do niej dostaje aria-current
 * @prop {string} version — wersja aplikacji pod nazwą
 *
 * @slot — widok bieżącej trasy
 * @slot overlay — nakładki przyklejone do dołu okna (powiadomienia,
 *   zaproszenia); dziedziczą `--qz-bottom-inset`, więc wchodzą nad pasek
 *   zakładek, a nie pod niego
 *
 * @fires qz-sign-in — gość kliknął „Zaloguj się"
 * @fires qz-sign-out — zalogowany kliknął „Wyloguj"
 */
export class QzAppShell extends LitElement {
  static properties = {
    user: { type: Object },
    loading: { type: Boolean },
    path: { type: String },
    version: { type: String },
  };

  static styles = [
    sharedStyles,
    css`
      :host {
        display: flex;
        flex-direction: column;
        min-height: 100dvh;

        --qz-tabs-height: 3.5rem;
      }

      /* Karta zaproszenia jest przyklejona do dołu okna — nad paskiem
         zakładek, nie pod nim. Zmienna przenika do niej przez shadow DOM. */
      @media (max-width: 640px) {
        :host([data-tabs]) {
          --qz-bottom-inset: var(--qz-tabs-height);
        }
      }

      /* --- nagłówek --- */

      header {
        position: sticky;
        top: 0;
        z-index: 20;
        display: flex;
        align-items: center;
        gap: var(--qz-space-6);
        padding: var(--qz-space-3) var(--qz-space-6);
        background: color-mix(in srgb, var(--qz-bg) 88%, transparent);
        backdrop-filter: blur(8px);
        border-bottom: 1px solid var(--qz-line);
      }

      .brand {
        display: flex;
        flex-direction: column;
        gap: 1px;
        text-decoration: none;
        color: inherit;
      }

      .brand-name {
        font-family: var(--qz-font-display);
        font-size: var(--qz-text-lg);
        line-height: 1;
        letter-spacing: -0.03em;
      }

      /* Nazwa mówi, czym produkt jest: quiz + -teka, czyli zbiór.
         Dwie grubości robią z tego widoczną informację, nie ozdobę. */
      .brand-name b {
        font-weight: 800;
      }

      .brand-name span {
        font-weight: 300;
      }

      /* Wersja siedzi tam, gdzie wcześniej tagline: ten sam drobny mono pod
         nazwą. Bez text-transform, bo wielkie V w numerze wersji wygląda
         jak literówka. */
      .brand-version {
        font-family: var(--qz-font-mono);
        font-size: 0.625rem;
        letter-spacing: var(--qz-label-tracking);
        color: var(--qz-ink-muted);
      }

      nav.top {
        display: flex;
        align-items: center;
        gap: var(--qz-space-1);
        margin-right: auto;
      }

      nav.top a {
        padding: var(--qz-space-2) var(--qz-space-3);
        border-radius: var(--qz-radius-sm);
        color: var(--qz-ink-muted);
        font-size: var(--qz-text-sm);
        font-weight: 500;
        text-decoration: none;
        transition:
          color var(--qz-transition),
          background var(--qz-transition);
      }

      nav.top a:hover {
        color: var(--qz-ink);
        background: var(--qz-surface-2);
      }

      nav.top a[aria-current='page'] {
        color: var(--qz-ink);
        background: var(--qz-surface);
        box-shadow: inset 0 0 0 1px var(--qz-line);
      }

      .header-right {
        display: flex;
        align-items: center;
        gap: var(--qz-space-3);
      }

      .account {
        display: flex;
        align-items: center;
        gap: var(--qz-space-3);
      }

      .account a {
        color: var(--qz-ink);
        font-size: var(--qz-text-sm);
        font-weight: 600;
        text-decoration: none;
      }

      .account a:hover {
        text-decoration: underline;
      }

      .role {
        font-family: var(--qz-font-mono);
        font-size: 0.625rem;
        letter-spacing: var(--qz-label-tracking);
        text-transform: uppercase;
        color: var(--qz-accent);
      }

      /* --- treść --- */

      /*
       * Kolumna elastyczna nie jest ozdobą: dzięki niej widok będący jedynym
       * dzieckiem main może zająć całą wolną wysokość (flex: 1) i rozłożyć
       * treść na ekranie, zamiast zbijać ją pod nagłówkiem i zostawiać pustkę
       * nad stopką. Korzysta z tego pojedynek.
       */
      main {
        display: flex;
        flex: 1;
        flex-direction: column;
        width: 100%;
        max-width: 68rem;
        margin: 0 auto;
        padding: var(--qz-space-8) var(--qz-space-6) var(--qz-space-16);
      }

      footer {
        padding: var(--qz-space-4) var(--qz-space-6);
        border-top: 1px solid var(--qz-line);
        text-align: center;
      }

      /* --- pasek zakładek (tylko telefon, tylko zalogowani) --- */

      /*
       * Na telefonie nawigacja schodzi z nagłówka na dół ekranu, w zasięg
       * kciuka — Quizotekę gra się w kilkuminutowych podejściach, najczęściej
       * w ruchu, i ekran po zalogowaniu ma się zachowywać jak aplikacja,
       * a nie jak strona. Na dużym ekranie pasek nie istnieje: tam nawigacja
       * zostaje w nagłówku, gdzie jej się szuka.
       */
      .tabs {
        display: none;
      }

      @media (max-width: 640px) {
        header {
          flex-wrap: wrap;
          gap: var(--qz-space-3);
          padding: var(--qz-space-2) var(--qz-space-4);
        }

        nav.top {
          order: 3;
          width: 100%;
          margin-right: 0;
          overflow-x: auto;
        }

        /* Zalogowany ma nawigację na dole — dublowanie jej w nagłówku
           zabierałoby połowę pierwszego ekranu. */
        :host([data-tabs]) nav.top {
          display: none;
        }

        .header-right {
          margin-left: auto;
          min-width: 0;
        }

        .account {
          min-width: 0;
          gap: var(--qz-space-2);
        }

        /*
         * Długa nazwa konta łamała nagłówek na dwa rzędy — a rząd nagłówka
         * to na telefonie kilka procent ekranu, których widok już nie odzyska.
         * Nazwa skraca się wielokropkiem, rola znika: mówi to samo, co
         * zakładka „panel" na dole.
         */
        .account a {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .role {
          display: none;
        }

        main {
          padding: var(--qz-space-4) var(--qz-space-4) var(--qz-space-6);
        }

        footer {
          padding: var(--qz-space-2) var(--qz-space-4);
        }

        /*
         * Stopka mówi tylko, że to wersja robocza — na telefonie zalogowanego
         * gracza zabierała tyle miejsca, co dwa warianty odpowiedzi. Zostaje
         * na stronie wizytówkowej, znika w aplikacji: tam dół ekranu należy
         * do paska zakładek.
         */
        :host([data-tabs]) footer {
          display: none;
        }

        /* Miejsce pod przyklejonym paskiem robi teraz treść, bo stopki,
           która robiła je wcześniej, już pod nią nie ma. */
        :host([data-tabs]) main {
          padding-bottom: calc(
            var(--qz-space-4) + var(--qz-tabs-height) + env(safe-area-inset-bottom, 0px)
          );
        }

        :host([data-tabs]) .tabs {
          position: fixed;
          right: 0;
          bottom: 0;
          left: 0;
          z-index: 30;
          display: flex;
          justify-content: space-around;
          padding-bottom: env(safe-area-inset-bottom, 0px);
          background: color-mix(in srgb, var(--qz-bg) 92%, transparent);
          backdrop-filter: blur(8px);
          border-top: 1px solid var(--qz-line);
        }

        .tabs a {
          display: flex;
          flex: 1;
          align-items: center;
          justify-content: center;
          min-height: var(--qz-tabs-height);
          padding: var(--qz-space-2);
          border-top: 2px solid transparent;
          color: var(--qz-ink-muted);
          font-family: var(--qz-font-mono);
          font-size: var(--qz-text-xs);
          letter-spacing: var(--qz-label-tracking);
          text-transform: uppercase;
          text-decoration: none;
        }

        /* Zakładka bieżąca dostaje kreskę u góry — jak zakładka w szufladzie. */
        .tabs a[aria-current='page'] {
          border-top-color: var(--qz-accent);
          color: var(--qz-ink);
        }
      }
    `,
  ];

  constructor() {
    super();
    this.user = null;
    this.loading = false;
    this.path = '';
    this.version = '';
  }

  /** Dopóki sesja się wczytuje, nie wiemy, kim jesteśmy — nikogo nie pokazujemy. */
  get #user() {
    return this.loading ? null : this.user;
  }

  /*
   * Pasek zakładek jest pozycjonowany względem okna, więc miejsce pod niego
   * musi zrobić powłoka, a nie sam pasek. Atrybut na hoście jest jedynym
   * sposobem, żeby ta sama informacja („jest zalogowany") dotarła do reguł
   * `main`, `nav.top` i nakładek ze slotu `overlay`.
   */
  willUpdate() {
    this.toggleAttribute('data-tabs', Boolean(this.#user));
  }

  #emit(type) {
    this.dispatchEvent(new CustomEvent(type, { bubbles: true, composed: true }));
  }

  #link(path, text) {
    const active = this.path === path;
    return html`<a href=${path} aria-current=${active ? 'page' : 'false'}>${text}</a>`;
  }

  #account() {
    if (this.loading) return nothing;

    const user = this.#user;
    if (!user) {
      return html`
        <div class="account">
          <qz-button size="sm" @click=${() => this.#emit('qz-sign-in')}>Zaloguj się</qz-button>
        </div>
      `;
    }

    return html`
      <div class="account">
        <a href="/profil">
          ${user.displayName || user.name}
          ${
            user.role !== 'player'
              ? html`<span class="role">${ROLE_NAMES[user.role] ?? user.role}</span>`
              : nothing
          }
        </a>
        <qz-button variant="ghost" size="sm" @click=${() => this.#emit('qz-sign-out')}>
          Wyloguj
        </qz-button>
      </div>
    `;
  }

  render() {
    const user = this.#user;
    const admin = hasRole(user, 'admin');

    return html`
      <header>
        <a class="brand" href="/">
          <span class="brand-name"><b>Quiz</b><span>oteka</span></span>
          ${
            this.version
              ? html`<span class="brand-version" title="Wersja aplikacji"
                  >v${this.version}</span
                >`
              : nothing
          }
        </a>

        <nav class="top">
          ${user ? [this.#link('/graj', 'Graj'), this.#link('/znajomi', 'Znajomi')] : nothing}
          ${admin ? [this.#link('/tworz', 'Twórz'), this.#link('/admin', 'Panel')] : nothing}
        </nav>

        <div class="header-right">${this.#account()}</div>
      </header>

      <main><slot></slot></main>

      ${
        user
          ? html`
              <nav class="tabs" aria-label="Nawigacja aplikacji">
                ${this.#link('/graj', 'graj')} ${this.#link('/znajomi', 'znajomi')}
                ${this.#link('/profil', 'profil')}
                ${admin ? this.#link('/admin', 'panel') : nothing}
              </nav>
            `
          : nothing
      }

      <footer>
        <span class="label">Quizoteka · wersja robocza</span>
      </footer>

      <slot name="overlay"></slot>
    `;
  }
}

customElements.define('qz-app-shell', QzAppShell);
